import { useCallback, useContext, useEffect, useRef } from 'react'

import { EDITOR_REQUIRED_ANCHORS, EDITOR_TOOLS, PanelType } from '@editor/config/constants'
import { useGlobalModalContext } from '@editor/contexts/GlobalModal'
import { UserContext } from '@editor/contexts/Users'
import { Editor } from '@editor/interfaces/canvas'
import { EditorConfig } from '@editor/interfaces/editor'
import { setAttentionText } from '@editor/pages/projects/common/AttentionText/store/attentionText'
import { openPanel } from '@editor/pages/projects/editor/store/editor'
import { createMaskingRegion } from '@editor/services/MaskingRegion'
import { withPromiseContained } from '@editor/services/Util'
import { useAppDispatch, useAppSelector } from '@editor/store/app'
import { toaster } from '@editor/system/toaster'
import mixpanel from 'mixpanel-browser'
import { useLocation, useParams } from 'react-router'

import { addMaskRegion, clearSavingMask, copySavingMask, resetWorking } from '../store'

/**
 * Configure the editor for PCD Trim tool.
 */
export default function useEditor({ selectedTool, isPreviousTool, changeTool }: Editor): EditorConfig {
  // Toast
  const successToastIdRef = useRef<string>(undefined)
  const infoToastIdRef = useRef<string>(undefined)

  // URL Params
  const { project_id } = useParams<{ project_id: string }>()
  const location = useLocation()
  const queries = new URLSearchParams(location.search)
  const inspection_area_id = queries.get('area')

  // Store
  const dispatch = useAppDispatch()
  const workingMask = useAppSelector((state) => state.maskPCD.workingMaskCuboid)
  const editingMask = useAppSelector((state) => state.maskPCD.workingMask)
  const maskRegions = useAppSelector((state) => state.maskPCD.regions)

  // Context
  const { getAccessToken } = useContext(UserContext)
  const { showErrorModal } = useGlobalModalContext()

  // Vars
  const isToolSelected = selectedTool === EDITOR_TOOLS.PCD_TRIM_CUBOID
  const isDirty = !!workingMask && workingMask.points.length > 0
  const isSaveable = !!workingMask && workingMask.points.length >= EDITOR_REQUIRED_ANCHORS.cuboid

  /**
   * Show attention text when tool is activated.
   */
  useEffect(() => {
    if (isToolSelected) {
      dispatch(
        setAttentionText({
          message:
            '注目領域内の点のみが2次元に変換されます。\n' +
            '注目領域を作成するため、3点を選んで面を選択してください。その後、1点を選んで奥行きを設定してください。\n' +
            '調整完了後、右下の保存ボタンを押してください。',
          linkMessage: '詳細はこちら（操作動画）',
          linkUrl: 'https://datalabs-jp.notion.site/a176ae9a89b94aa1889f10c57742d862',
        }),
      )
      dispatch(openPanel(PanelType.Settings))
    }
  }, [dispatch, isToolSelected])

  /**
   * Handle saving the mask region.
   */
  const handleSave = useCallback(async () => {
    if (!editingMask || !project_id || !inspection_area_id) {
      return
    }

    const access_token = await getAccessToken()
    if (!access_token) {
      return
    }

    const maskId = editingMask.region_id

    await withPromiseContained(async () => {
      const cuboid = await createMaskingRegion(
        access_token,
        project_id,
        inspection_area_id,
        editingMask,
        showErrorModal,
      )

      if (!cuboid) {
        return
      }

      mixpanel.track('Created mask region', {
        'Number of mask region': maskRegions.length + 1,
      })

      dispatch(addMaskRegion(cuboid))

      if (infoToastIdRef.current) toaster.dismiss(infoToastIdRef.current)
      successToastIdRef.current = toaster.create({
        title: '注目領域設定の保存が完了しました',
        type: 'success',
        duration: 3000,
        closable: true,
      })
    }).finally(() => {
      dispatch(clearSavingMask([maskId]))
      dispatch(setAttentionText({ message: '' }))
    })
  }, [editingMask, project_id, inspection_area_id, maskRegions.length, dispatch, getAccessToken, showErrorModal])

  /**
   * Handle apply operation.
   */
  const handleApply = useCallback(async () => {
    dispatch(setAttentionText({ message: '注目領域設定の保存中です...' }))
    dispatch(copySavingMask())
    await handleSave()
    dispatch(setAttentionText({ message: '' }))
  }, [dispatch, handleSave])

  /**
   * Handle submit operation.
   * It's basically save + switch to MOVE/SELECT tool.
   */
  const handleSubmit = useCallback(() => {
    // Copy first since changeTool will reset the working mask and we need the data to save
    dispatch(copySavingMask())

    // Switch to MOVE tool
    changeTool(EDITOR_TOOLS.MOVE, true)

    if (isSaveable) {
      if (infoToastIdRef.current) toaster.dismiss(infoToastIdRef.current)
      infoToastIdRef.current = toaster.create({
        title: '注目領域設定の保存を開始しました',
        description:
          '注目領域設定の保存が進行中です。完了をお待ちの間、他のツールを引き続きご利用いただけます。完了次第、通知いたします。',
        type: 'info',
        duration: 5000,
        closable: true,
      })

      void handleSave()
    }
  }, [changeTool, handleSave, dispatch, isSaveable])

  /**
   * On change to different tool, reset the processing anchor.
   */
  useEffect(() => {
    if (!isToolSelected && isPreviousTool(EDITOR_TOOLS.PCD_TRIM_CUBOID) && selectedTool !== EDITOR_TOOLS.FOCUS) {
      dispatch(resetWorking())
    }
  }, [isToolSelected, selectedTool, isPreviousTool, dispatch])

  /**
   * Reset on unmount.
   */
  useEffect(
    () => () => {
      dispatch(resetWorking())
    },
    [dispatch],
  )

  return {
    buttons: {
      apply: {
        key: 'save-mask-region-apply',
        label: '保存',
        loadingLabel: '保存中',
        onClick: handleApply,
        isShown: () => isToolSelected,
        isLoading: () => false,
        isDisabled: () => !isSaveable,
      },
      submit: {
        key: 'mask-pcd-submit',
        label: isDirty ? '保存して終了' : undefined,
        loadingLabel: '保存中',
        isLoading: () => false,
        isShown: () => isToolSelected,
        isDisabled: () => isDirty && !isSaveable,
        onClick: handleSubmit,
      },
      reset: {
        onClick: () => dispatch(resetWorking()),
        isShown: () => isToolSelected,
        isDisabled: () => !isSaveable,
      },
    },
  }
}
